import { supabase } from '@/integrations/supabase/client';

// Generic utility to load/save settings from the app_settings table.
// Each setting is stored as a JSONB value under a unique key.

export async function loadSetting<T>(key: string): Promise<T | null> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', key)
      .maybeSingle();
    if (error || !data) return null;
    return data.value as T;
  } catch {
    return null;
  }
}

export async function saveSetting<T>(key: string, value: T): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('app_settings')
      .upsert(
        { key, value: value as any, updated_at: new Date().toISOString() },
        { onConflict: 'key,company_id' }
      );
    return !error;
  } catch {
    return false;
  }
}

export async function loadAllSettings(): Promise<Record<string, any>> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('key, value');
    if (error || !data) return {};
    const result: Record<string, any> = {};
    data.forEach((row: any) => {
      result[row.key] = row.value;
    });
    return result;
  } catch {
    return {};
  }
}

// Settings keys used across the app
export const SETTINGS_KEYS = {
  GENERAL: 'general_settings',
  STATUSES: 'custom_statuses',
  RETURN_STATUSES: 'return_statuses',
  BRANCHES: 'branches',
  PERMISSIONS: 'permissions',
  ROLES: 'permission_roles',
  SCREENS: 'permission_screens',
  ACTIONS: 'permission_actions',
  TRACKING_CONFIG: 'tracking_config',
  COUNTRIES: 'countries_data',
} as const;

// Initialize all settings from DB into localStorage (call once on login)
export async function initAllSettingsFromDb(): Promise<void> {
  const all = await loadAllSettings();
  
  if (all[SETTINGS_KEYS.GENERAL]) {
    localStorage.setItem('general_settings', JSON.stringify(all[SETTINGS_KEYS.GENERAL]));
    window.dispatchEvent(new Event('general-settings-changed'));
  }
  if (all[SETTINGS_KEYS.STATUSES]) {
    localStorage.setItem('custom_statuses', JSON.stringify(all[SETTINGS_KEYS.STATUSES]));
  }
  if (all[SETTINGS_KEYS.RETURN_STATUSES]) {
    localStorage.setItem('return_statuses', JSON.stringify(all[SETTINGS_KEYS.RETURN_STATUSES]));
  }
  if (all[SETTINGS_KEYS.BRANCHES]) {
    localStorage.setItem('branches', JSON.stringify(all[SETTINGS_KEYS.BRANCHES]));
  }
  if (all[SETTINGS_KEYS.PERMISSIONS]) {
    localStorage.setItem('app_role_permissions', JSON.stringify(all[SETTINGS_KEYS.PERMISSIONS]));
  }
  if (all[SETTINGS_KEYS.ROLES]) {
    localStorage.setItem('app_permission_roles', JSON.stringify(all[SETTINGS_KEYS.ROLES]));
  }
  if (all[SETTINGS_KEYS.SCREENS]) {
    localStorage.setItem('app_permission_screens', JSON.stringify(all[SETTINGS_KEYS.SCREENS]));
  }
  if (all[SETTINGS_KEYS.ACTIONS]) {
    localStorage.setItem('app_permission_actions', JSON.stringify(all[SETTINGS_KEYS.ACTIONS]));
  }
  if (all[SETTINGS_KEYS.TRACKING_CONFIG]) {
    localStorage.setItem('tracking_config', JSON.stringify(all[SETTINGS_KEYS.TRACKING_CONFIG]));
  }
  if (all[SETTINGS_KEYS.COUNTRIES]) {
    localStorage.setItem('countries_data', JSON.stringify(all[SETTINGS_KEYS.COUNTRIES]));
    localStorage.setItem('countries_version', '18');
    window.dispatchEvent(new Event('countries-updated'));
  }
}
